import React, { useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ShieldAlert } from "lucide-react";

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-2xl font-bold text-[#012d5c] mb-4 pb-2 border-b border-gray-100">
      {title}
    </h2>
    <div className="space-y-4 text-gray-700 leading-relaxed">{children}</div>
  </section>
);

const ZeroTolerance = () => {
  useEffect(() => {
    document.title = "Zero Tolerance Policy | Integro Clinics";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content =
      "Integro Clinics operates a zero tolerance policy. All patients, staff and visitors are expected to treat each other with respect and dignity.";
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      {/* Hero */}
      <div
        className="bg-[#012d5c] py-20"
        style={{
          backgroundImage: "linear-gradient(rgba(1,45,92,0.88),rgba(1,45,92,0.88)),url('https://images.pexels.com/photos/4031818/pexels-photo-4031818.jpeg?auto=compress&cs=tinysrgb&w=1920&h=900&fit=crop')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <span className="inline-block bg-[#4caf50] text-white text-sm font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-5">
            Legal
          </span>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Zero Tolerance Policy
          </h1>
          <p className="text-gray-300 max-w-2xl mx-auto text-lg">
            Integro Clinics is committed to providing a safe environment for patients and staff. We will not tolerate abusive, threatening or violent behaviour.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">

            {/* Alert box */}
            <div className="bg-[#fff3cd] border border-[#ffc107]/30 rounded-xl p-6 mb-10 flex gap-4">
              <ShieldAlert className="w-6 h-6 text-[#856404] shrink-0 mt-0.5" />
              <p className="text-[#856404] font-medium text-sm leading-relaxed">
                Integro Clinics maintains a zero tolerance policy in line with national healthcare guidance. This policy applies to all interactions with our service, including in person, by telephone, by email and on online platforms.
              </p>
            </div>

            <Section title="Our Commitment">
              <p>
                Integro Clinics is committed to delivering high-quality care in a safe environment. We recognise that our staff often work in challenging situations and handle complex, sensitive matters. They have the right to perform their duties without experiencing abusive, threatening, or violent behaviour of any kind.
              </p>
              <p>
                We ask all patients, carers and visitors to treat our staff with the same courtesy and respect that we aim to provide to you. Mutual respect is the foundation of a positive clinical relationship.
              </p>
            </Section>

            <Section title="What We Expect">
              <p>
                We expect everyone who interacts with Integro Clinics — whether online, by telephone or in any other format — to behave in a respectful and reasonable manner towards our staff and other patients.
              </p>
              <p>
                We understand that patients may be distressed, frustrated or unwell. We make every effort to handle concerns with compassion and professionalism. However, even in difficult circumstances, abusive behaviour directed at our team is not acceptable.
              </p>
            </Section>

            <Section title="Unacceptable Behaviour">
              <p>The following behaviours are not tolerated under any circumstances:</p>
              <ul className="list-none space-y-2 mt-2">
                {[
                  "Shouting at, intimidating or verbally abusing members of staff.",
                  "Using offensive, racist, sexist or discriminatory language.",
                  "Sexual harassment or inappropriate behaviour towards staff.",
                  "Threatening language or gestures — whether in person, by phone, email or online.",
                  "Persistent unrealistic demands or behaviour designed to manipulate or pressure staff.",
                  "Attempting to obtain services, medication, refunds or expedited treatment through inappropriate pressure.",
                  "Submitting fraudulent or deliberately misleading information as part of a service request.",
                  "Disruptive behaviour that interferes with the care of other patients.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-red-500 mt-1 shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section title="Threats, Violence and Abuse">
              <p>
                We treat all threats of harm with the utmost seriousness. Any communication — regardless of the channel through which it is received — that contains a threat to harm staff, other patients or any individual will be reported to the relevant authorities. We have a legal obligation to report safety concerns to the police where appropriate.
              </p>
              <p>
                This policy applies equally to interactions by telephone, email, online platforms and social media.
              </p>
            </Section>

            <Section title="Misuse of Self-Harm Claims">
              <p>
                Integro Clinics takes all genuine concerns about self-harm extremely seriously. Our team is trained to respond with care and compassion to patients who express distress.
              </p>
              <p>
                However, we reserve the right to cease contact with or discharge any patient who falsely uses a threat of self-harm in order to obtain services, medication, expedited treatment, refunds or other outcomes. Any patient we believe to be in genuine distress will be directed to appropriate emergency services.
              </p>
            </Section>

            <Section title="Consequences of Unacceptable Behaviour">
              <p>
                Where a patient or individual engages in unacceptable behaviour, Integro Clinics reserves the right to take appropriate action, which may include:
              </p>
              <ul className="list-none space-y-2 mt-2">
                {[
                  "Issuing a formal warning.",
                  "Restricting the means by which the individual may contact the clinic.",
                  "Suspending or terminating the individual's access to our services.",
                  "Reporting the behaviour to the relevant authorities.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-[#4caf50] mt-1 shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4">
                Any decision to restrict or terminate access to services will be communicated clearly to the patient, who will be advised to seek care through an alternative provider.
              </p>
            </Section>

            <Section title="Reporting Concerns">
              <p>
                If you witness or experience behaviour that you believe is threatening, abusive or inappropriate in relation to Integro Clinics, please report it to us immediately.
              </p>
              <p>
                If you believe you or others are in immediate danger, please contact the emergency services by calling 999.
              </p>
            </Section>

            <Section title="Contact Us">
              <p>
                If you have any questions about this policy or wish to raise a concern, please contact our team:
              </p>
              <div className="mt-4 bg-[#f8fafc] border border-gray-100 rounded-xl p-6 space-y-2">
                <p><strong>Email:</strong>{" "}
                  <a href="mailto:office@integroclinics.com" className="text-[#005eb8] hover:underline">
                    office@integroclinics.com
                  </a>
                </p>
              </div>
            </Section>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ZeroTolerance;
